import * as React from 'react';

import { Combobox, type ComboboxOption } from '@/components/combobox';

const TRADE_TYPES: readonly ComboboxOption[] = [
  { value: 'Buy', label: 'Buy' },
  { value: 'Sell', label: 'Sell' },
  { value: 'Spot', label: 'Spot' },
];

const COMMODITIES: readonly ComboboxOption[] = [
  { value: 'al', label: 'Aluminum' },
  { value: 'cu', label: 'Copper' },
  { value: 'fe', label: 'Iron' },
  { value: 'pb', label: 'Lead' },
  { value: 'ni', label: 'Nickel' },
  { value: 'sn', label: 'Tin' },
  { value: 'zn', label: 'Zinc' },
];

function PlainCell() {
  const [value, setValue] = React.useState<string | undefined>(undefined);
  return (
    <Combobox value={value} onChange={setValue} options={TRADE_TYPES} placeholder="Pick a side" />
  );
}

function AutoSearchCell() {
  const [value, setValue] = React.useState<string | undefined>(undefined);
  return (
    <Combobox
      value={value}
      onChange={setValue}
      options={COMMODITIES}
      placeholder="Pick a commodity"
    />
  );
}

function ForceSearchableCell() {
  const [value, setValue] = React.useState<string | undefined>(undefined);
  return (
    <Combobox
      value={value}
      onChange={setValue}
      options={TRADE_TYPES}
      placeholder="Searchable side"
      searchable
    />
  );
}

function ForceNoSearchCell() {
  const [value, setValue] = React.useState<string | undefined>(undefined);
  return (
    <Combobox
      value={value}
      onChange={setValue}
      options={COMMODITIES}
      placeholder="Plain commodity"
      searchable={false}
    />
  );
}

function PreselectedCell() {
  const [value, setValue] = React.useState<string | undefined>('Sell');
  return <Combobox value={value} onChange={setValue} options={TRADE_TYPES} />;
}

function DisabledCell() {
  return (
    <Combobox
      value={undefined}
      onChange={() => undefined}
      options={TRADE_TYPES}
      placeholder="Disabled"
      disabled
    />
  );
}

function InvalidCell() {
  return (
    <Combobox
      value={undefined}
      onChange={() => undefined}
      options={TRADE_TYPES}
      placeholder="Invalid"
      aria-invalid
    />
  );
}

function AsyncCell() {
  const [value, setValue] = React.useState<string | undefined>(undefined);
  const [query, setQuery] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return COMMODITIES;
    return COMMODITIES.filter((o) => o.label.toLowerCase().includes(q));
  }, [query]);
  React.useEffect(() => {
    if (query === '') return;
    setLoading(true);
    const t = setTimeout(() => {
      setLoading(false);
    }, 350);
    return () => {
      clearTimeout(t);
    };
  }, [query]);
  return (
    <Combobox
      value={value}
      onChange={setValue}
      options={filtered}
      searchValue={query}
      onSearchChange={setQuery}
      loading={loading}
      placeholder="Server commodity"
    />
  );
}

function MultiPlainCell() {
  const [value, setValue] = React.useState<string[]>([]);
  return (
    <Combobox
      multi
      value={value}
      onChange={setValue}
      options={TRADE_TYPES}
      placeholder="Pick sides"
    />
  );
}

function MultiSearchCell() {
  const [value, setValue] = React.useState<string[]>(['cu']);
  return (
    <Combobox
      multi
      value={value}
      onChange={setValue}
      options={COMMODITIES}
      placeholder="Pick metals"
    />
  );
}

interface ComboboxVariant {
  label: string;
  description: string;
  Cell: React.ComponentType;
}

const VARIANTS: readonly ComboboxVariant[] = [
  {
    label: 'plain (≤5 options)',
    description: 'Renders without a search input. Use for small, well-known enums.',
    Cell: PlainCell,
  },
  {
    label: 'auto-search (>5 options)',
    description: 'Search appears automatically once options pass the threshold.',
    Cell: AutoSearchCell,
  },
  {
    label: 'searchable={true}',
    description: 'Force the search input even on a short list.',
    Cell: ForceSearchableCell,
  },
  {
    label: 'searchable={false}',
    description: 'Suppress the search input even on a long list.',
    Cell: ForceNoSearchCell,
  },
  {
    label: 'preselected value',
    description: 'Trigger renders the selected label and a Clear item appears in the list.',
    Cell: PreselectedCell,
  },
  {
    label: 'async (server-driven)',
    description: 'Caller owns the query; cmdk filtering is disabled and loading is surfaced.',
    Cell: AsyncCell,
  },
  {
    label: 'disabled',
    description: 'Inert trigger; same affordance as a disabled Button.',
    Cell: DisabledCell,
  },
  {
    label: 'aria-invalid',
    description: 'Destructive border + ring; matches the other form controls.',
    Cell: InvalidCell,
  },
  {
    label: 'multi (plain)',
    description:
      'multi mode with a short option list. Each pick becomes a chip; the popover stays open until dismissed.',
    Cell: MultiPlainCell,
  },
  {
    label: 'multi (with search)',
    description:
      'multi mode over a long list. Backspace in the search clears nothing — chips are removed via their X button.',
    Cell: MultiSearchCell,
  },
];

export function ComboboxSection() {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Combobox</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          A single picker that scales from a 2-option dropdown to a 200-row server-search list.
          <strong className="text-foreground"> Prefer this over the native select</strong> in new
          code: <code className="font-mono">Combobox</code> for raw use,{' '}
          <code className="font-mono">ComboboxField</code> when wired into a{' '}
          <code className="font-mono">react-hook-form</code> form. Below, every variant is shown
          side-by-side so you can pick the right one.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {VARIANTS.map(({ label, description, Cell }) => (
          <div key={label} className="space-y-2">
            <div>
              <p className="text-foreground font-mono text-xs">{label}</p>
              <p className="text-muted-foreground text-xs">{description}</p>
            </div>
            <Cell />
          </div>
        ))}
      </div>
    </section>
  );
}
